// WebGL shaders for the CMYK halftone hero.
//
// Design notes for this revision:
// - No flicker anywhere: no per-frame grain, no random blotches, no sudden
//   plate-offset jumps. The baseline image is completely stable.
// - Baseline: clean CMYK halftone plates aligned on top of each other.
// - Hover: inside the cursor disc, each plate fans out into a fixed direction
//   (C ↖, M ↗, Y ↙, K ↘) proportional to disc strength, producing a very
//   obvious color separation. A hue rotation is layered on for extra "color
//   shift" impact. Chromatic aberration tracks pointer velocity.

export const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

export const FRAGMENT_SHADER = `
precision highp float;

varying vec2 v_uv;

uniform sampler2D u_source;
uniform vec2  u_resolution;    // canvas pixel size
uniform vec2  u_cursor;        // px, top-left origin
uniform float u_discRadius;    // px, 0..280 (in device px)
uniform float u_chroma;        // px, 0..12
uniform float u_zoom;          // 1.0..1.02
uniform float u_hoverStrength; // 0..1, easing follows the disc
uniform float u_dotPitch;      // px, cell size for halftone lattice

const float PI = 3.14159265359;

// Rotated halftone: returns 0..1 dot mask given a source coverage value.
float halftoneDot(vec2 fragPx, float angleDeg, float coverage, float pitch) {
  float a = radians(angleDeg);
  mat2 rot = mat2(cos(a), -sin(a), sin(a), cos(a));
  vec2 gridPx = rot * fragPx;
  vec2 cell = mod(gridPx, pitch) - pitch * 0.5;
  float d = length(cell);
  // Target radius so that dot area matches coverage.
  float r = sqrt(coverage) * (pitch * 0.5) * 1.15;
  return 1.0 - smoothstep(r - 1.0, r + 0.5, d);
}

// CMYK conversion from RGB.
vec4 rgbToCmyk(vec3 c) {
  float k = 1.0 - max(max(c.r, c.g), c.b);
  float invK = max(1.0 - k, 1e-4);
  float C = (1.0 - c.r - k) / invK;
  float M = (1.0 - c.g - k) / invK;
  float Y = (1.0 - c.b - k) / invK;
  return clamp(vec4(C, M, Y, k), 0.0, 1.0);
}

float samplePlate(vec2 fragPx, int planeIdx) {
  vec2 uv = fragPx / u_resolution;
  vec3 c = texture2D(u_source, uv).rgb;
  vec4 cmyk = rgbToCmyk(c);
  if (planeIdx == 0) return cmyk.x;
  if (planeIdx == 1) return cmyk.y;
  if (planeIdx == 2) return cmyk.z;
  return cmyk.w;
}

// Simple RGB → HSV → rotate hue → RGB, for the disc-only color shift.
vec3 rgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

void main() {
  vec2 fragPx = vec2(v_uv.x, 1.0 - v_uv.y) * u_resolution;

  // Disturbance disc mask (soft edge from 200/280 * outer to outer).
  float distToCursor = distance(fragPx, u_cursor);
  float discOuter = max(u_discRadius, 0.0001);
  float discInner = discOuter * (200.0 / 280.0);
  float insideDisc = 1.0 - smoothstep(discInner, discOuter, distToCursor);
  // Combined hover strength (disc AND easing envelope).
  float hover = insideDisc * u_hoverStrength;

  // Micro-zoom around cursor (max 1.02, only inside disc).
  float zoomAmt = mix(1.0, u_zoom, insideDisc);
  vec2 zoomedPx = (fragPx - u_cursor) / zoomAmt + u_cursor;

  // ---- Plate offsets ----
  // Baseline: zero (no random jitter, no flicker).
  // Hover: each plate fans out in a fixed direction, magnitude scales with hover.
  float spread = 30.0 * hover;                     // max ~30 px separation inside disc
  vec2 dirC = vec2(-0.7071,  0.7071);              // ↖
  vec2 dirM = vec2( 0.7071,  0.7071);              // ↗
  vec2 dirY = vec2(-0.7071, -0.7071);              // ↙
  vec2 dirK = vec2( 0.7071, -0.7071);              // ↘
  vec2 offC = dirC * spread;
  vec2 offM = dirM * spread;
  vec2 offY = dirY * spread;
  vec2 offK = dirK * spread;

  // Chromatic aberration inside disc.
  float ca = u_chroma * hover;
  offC += vec2(-ca,  ca * 0.35);
  offM += vec2( 0.0, -ca * 0.45);
  offY += vec2( ca,  ca * 0.35);

  float covC = samplePlate(zoomedPx + offC, 0);
  float covM = samplePlate(zoomedPx + offM, 1);
  float covY = samplePlate(zoomedPx + offY, 2);
  float covK = samplePlate(zoomedPx + offK, 3);

  float dC = halftoneDot(zoomedPx + offC, 15.0, covC, u_dotPitch);
  float dM = halftoneDot(zoomedPx + offM, 75.0, covM, u_dotPitch);
  float dY = halftoneDot(zoomedPx + offY,  0.0, covY, u_dotPitch);
  float dK = halftoneDot(zoomedPx + offK, 45.0, covK, u_dotPitch);

  // Subtractive ink model on white paper.
  vec3 paper = vec3(1.0);
  vec3 inkC = vec3(0.00, 0.72, 0.92);
  vec3 inkM = vec3(0.92, 0.10, 0.55);
  vec3 inkY = vec3(0.98, 0.86, 0.10);
  vec3 inkK = vec3(0.05, 0.06, 0.14);

  // Boost ink saturation inside the disc for a stronger color pop.
  vec3 popC = mix(inkC, vec3(0.00, 0.90, 1.00), hover);
  vec3 popM = mix(inkM, vec3(1.00, 0.00, 0.55), hover);
  vec3 popY = mix(inkY, vec3(1.00, 0.94, 0.00), hover);

  vec3 col = paper;
  col = mix(col, popC, dC * 0.90);
  col = mix(col, popM, dM * 0.90);
  col = mix(col, popY, dY * 0.85);
  col = mix(col, inkK, dK * 0.92);

  // Extra hue rotation inside the disc — makes the color change feel very obvious.
  if (hover > 0.001) {
    vec3 hsv = rgb2hsv(col);
    // Rotate hue up to ~90° at full hover.
    hsv.x = fract(hsv.x + 0.25 * hover);
    hsv.y = clamp(hsv.y * (1.0 + 0.35 * hover), 0.0, 1.0);
    vec3 rotated = hsv2rgb(hsv);
    col = mix(col, rotated, hover);
  }

  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;